"use client";

import {
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { matchResultToString } from "@/lib/sports";
import { Match, MatchStatus } from "@/types";
import { format } from "date-fns/format";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

interface MatchResultRowProps {
  match: Match;
  highlightTeamId?: string;
}

export function MatchResultRow({
  match,
  highlightTeamId,
}: MatchResultRowProps) {
  const t = useTranslations();
  const { teams } = useTeamsContext();
  const { locations } = useTournamentContext();

  const result = match.result || null;
  const isWalkover = result?.walkover === true;
  const isWinner =
    result && highlightTeamId && result.winnerId === highlightTeamId;
  const isLoser =
    result && highlightTeamId && result.winnerId !== highlightTeamId;

  // Check if this is a pending match (challenge or scheduled without result)
  const isPending =
    !result &&
    (match.status === MatchStatus.CHALLENGE ||
      match.status === MatchStatus.SCHEDULED);
  const isScheduled = match.status === MatchStatus.SCHEDULED;
  const isChallenge = match.status === MatchStatus.CHALLENGE;
  const isDeclined = match.status === MatchStatus.DECLINED;

  // Determine if highlighted team sent the challenge (is team1)
  const isChallengerTeam = highlightTeamId === match.team1Id;

  // For declined challenges: team1 is the challenger, team2 declined
  const isDeclinedByThisTeam = isDeclined && !isChallengerTeam;

  // Get opponent team
  const opponentTeam = useMemo(() => {
    if (!highlightTeamId) return null;
    const opponentId =
      match.team1Id === highlightTeamId ? match.team2Id : match.team1Id;
    return teams.find((t) => t.id === opponentId);
  }, [teams, match, highlightTeamId]);

  // Get both team names for declined challenges
  const team1 = useMemo(
    () => teams.find((t) => t.id === match.team1Id),
    [teams, match.team1Id],
  );
  const team2 = useMemo(
    () => teams.find((t) => t.id === match.team2Id),
    [teams, match.team2Id],
  );

  // Get location name
  const location = useMemo(() => {
    if (!match.locationId) return null;
    return locations.find((l) => l.id === match.locationId);
  }, [locations, match.locationId]);

  // Determine background and accent color based on match state
  const getRowStyles = () => {
    if (isWinner) return { bg: "bg-emerald-500/8", accent: "bg-emerald-500" };
    if (isLoser) return { bg: "bg-red-500/8", accent: "bg-red-500" };
    if (isPending) return { bg: "bg-blue-500/8", accent: "bg-blue-500" };
    if (isDeclined) return { bg: "bg-white/[0.03]", accent: "bg-white/10" };
    return { bg: "bg-white/[0.03]", accent: "bg-white/10" };
  };

  const { bg, accent } = getRowStyles();

  return (
    <div className={`relative overflow-hidden rounded-xl ${bg}`}>
      {/* Left accent bar for win/loss/pending */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />

      <div className="flex flex-col gap-1 py-3 pl-4 pr-3">
        {/* Row 1: opponent name */}
        <span className="text-[15px] font-semibold text-white/90 truncate">
          {opponentTeam?.name || t("teams.unnamed")}
        </span>

        {/* Row 2: badge and score/status */}
        <div className="flex items-center justify-between gap-2">
          {/* Status badge */}
          {result ? (
            <span
              className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                isWinner
                  ? "bg-emerald-500/20 text-emerald-400"
                  : isLoser
                    ? "bg-red-500/20 text-red-400"
                    : "bg-white/10 text-white/50"
              }`}
            >
              {isWinner
                ? isWalkover
                  ? t("matches.victoryWO")
                  : t("matches.victory")
                : isLoser
                  ? isWalkover
                    ? t("matches.defeatWO")
                    : t("matches.defeat")
                  : t("matches.draw")}
            </span>
          ) : isPending ? (
            <div className="flex flex-col gap-1">
              <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                {isScheduled
                  ? t("matches.status.scheduled")
                  : isChallengerTeam
                    ? t("matches.status.challengeSentLong")
                    : t("matches.pendingChallenge")}
              </span>
              {!isScheduled && (
                <span className="shrink-0 text-[11px] uppercase tracking-wide px-1.5 py-0.5 text-white/50">
                  {t("matches.needsMatchDate")}
                </span>
              )}
            </div>
          ) : isDeclined ? (
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/50">
              {t("matches.status.declined")}
            </span>
          ) : null}

          {/* Score display or pending indicator */}
          <div className="shrink-0">
            {result ? (
              <span
                className={`font-mono text-sm font-bold tabular-nums ${
                  isWinner
                    ? "text-emerald-400"
                    : isLoser
                      ? "text-red-400"
                      : "text-white/70"
                }`}
              >
                {matchResultToString(result, match.sport)}
              </span>
            ) : isPending ? (
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-blue-400/60"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            ) : (
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5">
                <span className="text-xs text-white/30">?</span>
              </div>
            )}
          </div>
        </div>

        {/* Row 3: date/time info based on status */}
        <div className="flex items-center gap-1.5 text-xs text-white/40">
          {isPending ? (
            isScheduled && match.matchDate ? (
              // Scheduled: show match date and location
              <>
                <svg
                  className="w-3 h-3 text-blue-400/50 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-blue-400/70">
                  {format(match.matchDate, "dd.MM. HH.mm")}
                </span>
                {location && (
                  <>
                    <span className="text-white/20">·</span>
                    <span className="truncate text-white/50">
                      {location.name}
                    </span>
                  </>
                )}
              </>
            ) : isChallenge ? (
              // Challenge: show when it was sent and expiry
              <>
                <svg
                  className="w-3 h-3 text-blue-400/50 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-blue-400/70">
                  {t("matches.sentOn", {
                    date: format(match.createdAt, "dd.MM."),
                  })}
                </span>
                <span className="text-white/20">·</span>
                <span className="truncate text-white/40">
                  {t("matches.expiresOn", {
                    date: format(match.challengeExpiresAt, "dd.MM. HH.mm"),
                  })}
                </span>
              </>
            ) : (
              <span>-</span>
            )
          ) : isDeclined ? (
            // Declined: show who challenged whom and when
            <>
              <span className="truncate text-white/50">
                {t("matches.declinedChallenge", {
                  decliningTeam: team2?.name || t("teams.unnamed"),
                  challengingTeam: team1?.name || t("teams.unnamed"),
                })}
              </span>
              <span className="text-white/20">·</span>
              <span className="text-white/40">
                {format(match.createdAt, "dd.MM.")}
              </span>
            </>
          ) : (
            // Completed match: show date and location
            <>
              <span>
                {match.matchDate
                  ? format(match.matchDate, "dd.MM. HH.mm")
                  : "-"}
              </span>
              {location && (
                <>
                  <span>·</span>
                  <span className="truncate">{location.name}</span>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
